import { useMemo, useState } from "react";
import { gstatus } from "@/components/graphisme/gUtils";

const KANBAN_COLS = [
  ["chiffrer", "À chiffrer", ["nouvelle_demande", "a_analyser", "infos_manquantes", "devis_a_creer"]],
  ["devis", "Devis en cours", ["devis_envoye", "attente_validation_devis", "devis_accepte", "acompte_a_regler"]],
  ["planifier", "À planifier", ["a_planifier", "planifie_partiel"]],
  ["production", "En production", ["planifie", "en_cours", "en_modification"]],
  ["validation", "Validation client", ["pret_a_envoyer", "envoye_validation", "modifications_demandees", "partiellement_valide"]],
  ["termines", "Terminés", ["projet_valide", "a_facturer", "facture", "attente_reglement", "paye", "livre", "termine"]],
];

function MiniCard({ p, onOpen }) {
  const st = gstatus(p.status);
  return (
    <button onClick={() => onOpen(p)} data-testid={`gview-card-${p.id}`}
      className="w-full text-left bg-white border border-neutral-200 rounded-lg px-3 py-2 hover:border-[#93C5FD] hover:shadow-sm transition-all">
      <p className="text-xs font-bold text-[#0F172A] truncate">{p.name}</p>
      <p className="text-[10px] text-neutral-400 truncate">{p.client_name}{p.deadline ? ` · ${p.deadline.slice(8, 10)}/${p.deadline.slice(5, 7)}` : ""}</p>
      <span className={`inline-block mt-1 text-[9px] font-semibold border rounded-full px-1.5 py-0.5 ${st.cls}`}>{st.label}</span>
    </button>
  );
}

export function KanbanView({ projects, onOpen }) {
  return (
    <div className="flex gap-3 overflow-x-auto pb-3 h-full" data-testid="gkanban">
      {KANBAN_COLS.map(([id, label, statuses]) => {
        const items = projects.filter((p) => statuses.includes(p.status));
        return (
          <div key={id} className="w-60 shrink-0 bg-neutral-50 border border-neutral-200 rounded-xl flex flex-col" data-testid={`gkanban-col-${id}`}>
            <p className="text-xs font-bold text-neutral-600 px-3 py-2.5 border-b border-neutral-200">{label} <span className="text-neutral-300 font-semibold">{items.length}</span></p>
            <div className="flex-1 overflow-y-auto p-2 space-y-2">
              {items.map((p) => <MiniCard key={p.id} p={p} onOpen={onOpen} />)}
              {items.length === 0 && <p className="text-[10px] text-neutral-300 text-center py-4">—</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function ClientView({ projects, onOpen }) {
  const groups = useMemo(() => {
    const g = {};
    for (const p of projects) (g[p.client_name || "Sans client"] = g[p.client_name || "Sans client"] || []).push(p);
    return Object.entries(g).sort((a, b) => b[1].length - a[1].length);
  }, [projects]);
  return (
    <div className="space-y-3 pb-3" data-testid="gclientview">
      {groups.map(([name, items]) => (
        <div key={name} className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden" data-testid={`gclient-group-${name.slice(0, 12)}`}>
          <p className="text-xs font-bold text-neutral-600 px-4 py-2.5 border-b border-neutral-100">{name} <span className="text-neutral-300 font-semibold">{items.length} projet(s)</span></p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 p-3">
            {items.map((p) => <MiniCard key={p.id} p={p} onOpen={onOpen} />)}
          </div>
        </div>
      ))}
      {groups.length === 0 && <p className="text-xs text-neutral-400 text-center py-8">Aucun projet.</p>}
    </div>
  );
}

export function CalendarView({ projects, onOpen }) {
  const [cursor, setCursor] = useState(() => new Date());
  const slotsByDay = useMemo(() => {
    const m = {};
    for (const p of projects) {
      for (const s of p.planned_slots || []) {
        if (!s.date) continue;
        (m[s.date] = m[s.date] || []).push({ project: p, slot: s });
      }
    }
    return m;
  }, [projects]);
  const y = cursor.getFullYear(), mo = cursor.getMonth();
  const first = new Date(y, mo, 1);
  const startOffset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(y, mo + 1, 0).getDate();
  const cells = [...Array(startOffset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const monthLabel = cursor.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid="gcalendarview">
      <div className="flex items-center justify-between mb-3">
        <p className="font-heading font-bold text-sm capitalize">{monthLabel}</p>
        <div className="flex gap-1">
          <button onClick={() => setCursor(new Date(y, mo - 1, 1))} className="text-xs border border-neutral-200 rounded-md px-2 py-1 hover:bg-neutral-50" data-testid="gcal-prev">←</button>
          <button onClick={() => setCursor(new Date())} className="text-xs border border-neutral-200 rounded-md px-2 py-1 hover:bg-neutral-50" data-testid="gcal-today">Aujourd'hui</button>
          <button onClick={() => setCursor(new Date(y, mo + 1, 1))} className="text-xs border border-neutral-200 rounded-md px-2 py-1 hover:bg-neutral-50" data-testid="gcal-next">→</button>
        </div>
      </div>
      <div className="grid grid-cols-7 text-center text-[10px] font-semibold text-neutral-400 mb-1">
        {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((d) => <span key={d}>{d}</span>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d === null) return <div key={`e${i}`} />;
          const key = `${y}-${String(mo + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const items = slotsByDay[key] || [];
          return (
            <div key={key} className={`min-h-[64px] border rounded-lg p-1 ${items.length ? "border-orange-200 bg-orange-50/40" : "border-neutral-100"}`} data-testid={`gcal-day-${key}`}>
              <p className="text-[10px] font-semibold text-neutral-500">{d}</p>
              {items.slice(0, 2).map(({ project, slot }, k) => (
                <button key={k} onClick={() => onOpen(project)} data-testid={`gcal-slot-${project.id}-${k}`}
                  className={`w-full text-left text-[9px] rounded px-1 py-0.5 mt-0.5 truncate ${slot.booking_status === "provisoire" ? "bg-amber-100 text-amber-800 border border-dashed border-amber-300" : "bg-orange-100 text-orange-800"}`}>
                  {Math.floor(slot.start_min / 60)}h{String(slot.start_min % 60).padStart(2, "0")} {project.name}
                </button>
              ))}
              {items.length > 2 && <p className="text-[9px] text-neutral-400 mt-0.5">+{items.length - 2}</p>}
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-neutral-400 mt-2">Orange plein = créneau confirmé · hachuré ambre = réservation provisoire.</p>
    </div>
  );
}
